import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { useCreateTicket, useTicketCategories } from '../hooks/useTickets';
import { useSites } from '../hooks/useSites';

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical (P1) — System down / safety risk' },
  { value: 'high', label: 'High (P2) — Major functionality impacted' },
  { value: 'medium', label: 'Medium (P3) — Partial issue / workaround exists' },
  { value: 'low', label: 'Low (P4) — Cosmetic / request' },
];

const inputClassName =
  'w-full bg-ems-bg border border-ems-border rounded-lg px-4 py-2.5 text-sm text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none focus:border-ems-green transition-colors';

const labelClassName =
  'block text-xs font-semibold text-ems-text-muted uppercase mb-1.5';

export function TicketCreate() {
  const navigate = useNavigate();
  const createTicket = useCreateTicket();
  const { data: categories } = useTicketCategories();
  const { data: sitesData } = useSites({ limit: 200 });

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [categoryId, setCategoryId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [error, setError] = useState('');

  const isValid = subject.trim().length > 0 && description.trim().length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    try {
      const ticket = await createTicket.mutateAsync({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        site_id: siteId && siteId !== 'cloud-hmi' ? siteId : undefined,
        category_id: categoryId || undefined,
        source: 'customer_portal',
      });
      navigate(`/tickets/${ticket.ticket_id}`);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to create ticket. Please try again.';
      setError(message);
    }
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/tickets')}
        className="flex items-center gap-1.5 text-sm text-ems-text-muted hover:text-ems-green transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Tickets
      </button>

      {/* Header */}
      <h1 className="text-2xl font-bold text-ems-text-primary mb-8">
        Submit a Ticket
      </h1>

      {/* Error alert */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Subject */}
        <div>
          <label htmlFor="subject" className={labelClassName}>
            Subject *
          </label>
          <input
            id="subject"
            type="text"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of the issue"
            className={inputClassName}
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className={labelClassName}>
            Description *
          </label>
          <textarea
            id="description"
            required
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue in detail..."
            className={inputClassName + ' resize-y'}
          />
        </div>

        {/* Priority */}
        <div>
          <label htmlFor="priority" className={labelClassName}>
            Priority
          </label>
          <select
            id="priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className={inputClassName}
          >
            {PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div>
          <label htmlFor="category" className={labelClassName}>
            Category
          </label>
          <select
            id="category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className={inputClassName}
          >
            <option value="">Select a category...</option>
            {categories?.map((cat) => (
              <option key={cat.category_id} value={cat.category_id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Related Site */}
        <div>
          <label htmlFor="site" className={labelClassName}>
            Related Site
          </label>
          <select
            id="site"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className={inputClassName}
          >
            <option value="">Select a site...</option>
            <option value="cloud-hmi">Cloud HMI / Portal</option>
            {sitesData?.sites?.map((site) => (
              <option key={site.site_id} value={site.site_id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>

        {/* Submit button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={!isValid || createTicket.isPending}
            className="flex items-center gap-2 px-6 py-2.5 bg-ems-green text-ems-bg rounded-lg text-sm font-semibold hover:bg-ems-green-bright disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {createTicket.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {createTicket.isPending ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}
